import { geoAlbersUsa } from "d3-geo";
import { FC, useCallback, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";

import { Box, Spinner } from "@/components/ui";

import {
  DISTRICTS_URL,
  GeoFeature,
  geographyStyle,
  HatchDefs,
  MAP_HEIGHT,
  MAP_WIDTH,
  MapTooltip,
  SELECTED_STROKE,
  STATES_URL,
  TooltipState,
  uniqueHatches,
} from "./MapParts";
import type { Paint } from "./types";

/* ── Types ─────────────────────────────────────── */

export interface DistrictMapProps {
  /** A district was clicked: its state's postal code; null when zoomed. */
  onSelect: ((state: string) => void) | null;
  /** Paint per district id ("GA-06"). */
  paints: Record<string, Paint>;
  /** The zoomed state and its shape (fits the projection); null for the
      nation. */
  state: { abbr: string; feature: GeoJSON.Feature | null; fips: string } | null;
}

/* ── Constants ─────────────────────────────────── */

const PADDING = 16;

const outlineStyle = (stroke: string, strokeWidth: number) => {
  const base: React.CSSProperties = {
    fill: "none",
    outline: "none",
    pointerEvents: "none",
    stroke,
    strokeWidth,
  };
  return { default: base, hover: base, pressed: base };
};

/* ── Component ─────────────────────────────────── */

/** House districts as drawn for 2026, nationally or within one state. */
const DistrictMap: FC<DistrictMapProps> = ({ onSelect, paints, state }) => {
  const [loaded, setLoaded] = useState(false);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const hatches = useMemo(() => uniqueHatches(Object.values(paints)), [paints]);
  const abbr = state?.abbr ?? null;
  const fips = state?.fips ?? null;
  const feature = state?.feature ?? null;

  // Kept stable: react-simple-maps refetches a map file whenever
  // parseGeographies changes, and the tooltip re-renders on every move.
  const parseDistricts = useCallback(
    (features: GeoJSON.Feature[]) => {
      setLoaded(true);
      return abbr
        ? features.filter((f) => String(f.id).startsWith(`${abbr}-`))
        : features;
    },
    [abbr]
  );
  const parseOutlines = useCallback(
    (features: GeoJSON.Feature[]) =>
      fips ? features.filter((f) => String(f.id) === fips) : features,
    [fips]
  );

  // The national view matches the state map's projection; a state view
  // fits the state (Alaska and Hawaii included).
  const projection = useMemo(
    () =>
      feature
        ? geoAlbersUsa().fitExtent(
            [
              [PADDING, PADDING],
              [MAP_WIDTH - PADDING, MAP_HEIGHT - PADDING],
            ],
            feature
          )
        : abbr
          ? null
          : geoAlbersUsa()
              .scale(1000)
              .translate([MAP_WIDTH / 2, MAP_HEIGHT / 2]),
    [abbr, feature]
  );

  if (!projection) {
    return (
      <Box
        className="w-full items-center justify-center"
        style={{ aspectRatio: MAP_WIDTH / MAP_HEIGHT }}
      >
        <Spinner color="rgb(var(--color-primary-500))" size="small" />
      </Box>
    );
  }

  return (
    <Box
      style={{
        aspectRatio: MAP_WIDTH / MAP_HEIGHT,
        position: "relative",
        width: "100%",
      }}
    >
      {!loaded && (
        <Box className="absolute inset-0 items-center justify-center">
          <Spinner color="rgb(var(--color-primary-500))" size="small" />
        </Box>
      )}
      <ComposableMap
        height={MAP_HEIGHT}
        // @ts-expect-error react-simple-maps v3 uses a function projection as the d3 projection itself; its types expect a factory
        projection={projection}
        style={{ display: "block", height: "100%", width: "100%" }}
        width={MAP_WIDTH}
      >
        <HatchDefs hatches={hatches} />
        <Geographies
          geography={DISTRICTS_URL}
          parseGeographies={parseDistricts}
        >
          {({ geographies }: { geographies: GeoFeature[] }) =>
            geographies.map((geo) => {
              const paint = paints[geo.id];
              const show = (e: React.MouseEvent) =>
                setTooltip({
                  text: paint?.tooltip ?? geo.id,
                  x: e.clientX,
                  y: e.clientY,
                });
              return (
                <Geography
                  geography={geo}
                  key={geo.rsmKey}
                  onClick={
                    onSelect ? () => onSelect(geo.id.slice(0, 2)) : undefined
                  }
                  onMouseEnter={show}
                  onMouseLeave={() => setTooltip(null)}
                  onMouseMove={show}
                  style={geographyStyle(
                    paint,
                    false,
                    onSelect !== null,
                    abbr ? 0.75 : 0.3
                  )}
                />
              );
            })
          }
        </Geographies>
        <Geographies geography={STATES_URL} parseGeographies={parseOutlines}>
          {({ geographies }: { geographies: GeoFeature[] }) =>
            geographies.map((geo) => (
              <Geography
                geography={geo}
                key={geo.rsmKey}
                style={
                  abbr
                    ? outlineStyle(SELECTED_STROKE, 1.25)
                    : outlineStyle("#fff", 1)
                }
              />
            ))
          }
        </Geographies>
      </ComposableMap>
      <MapTooltip tooltip={tooltip} />
    </Box>
  );
};

export default DistrictMap;
